import { type EmailValidationResponse } from '@atlaskit/user-picker';
import { messages } from '../i18n';
import { type MessageDescriptor } from 'react-intl-next';

export type ValidationState = {
	validationState: 'default' | 'error' | 'success';
	reason?: 'invalid-email' | 'overflow' | 'inviting-self';
	errorMessage?: MessageDescriptor;
};

export const VALIDATION_OVERFLOW_ERROR: ValidationState = {
	validationState: 'error',
	reason: 'overflow',
	errorMessage: messages.tooManyEmailAddresses,
};

export const VALIDATION_OVERFLOW_ERROR_FOR_PROJECTS: ValidationState = {
	validationState: 'error',
	reason: 'overflow',
	errorMessage: messages.tooManyEmailAddressesInProject,
};

export const VALIDATION_INVALID_EMAIL: ValidationState = {
	validationState: 'error',
	reason: 'invalid-email',
	errorMessage: messages.invalidMultipleEmailMessage,
};
export const VALIDATION_DEFAULT: ValidationState[] = [
	{
		validationState: 'default',
	},
];

export const VALIDATION_ERROR_INVITING_SELF: ValidationState = {
	validationState: 'error',
	reason: 'inviting-self',
	errorMessage: messages.inviteSelfMessage,
};

// Email Validations
// Extends default validation found in /packages/elements/user-picker/src/components/emailValidation.ts
// by checking that the domain does not end in a symbol

const validRegex =
	/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;

const potentialRegex = /^[^\s@]+@[^\s@]*$/i;

export const validateEmail = (inputText: string): EmailValidationResponse => {
	// Email will display as an option in the user picker and willbe selectable
	if (inputText.match(validRegex)) {
		return 'VALID';
	}
	// Potential emails will not be domain auto completed by the user picker (validation check is still completed on these emails)
	if (inputText.match(potentialRegex)) {
		return 'POTENTIAL';
	}
	// Invalid emails will have the domain auto complete option from the user picker
	return 'INVALID';
};

export const hasError = (validationList: ValidationState[], reason?: string) =>
	validationList.reduce(
		(error, validationItem) =>
			error ||
			(!!reason &&
				validationItem.reason === reason &&
				validationItem.validationState === 'error') ||
			validationItem.validationState === 'error',
		false,
	);

export const determineValidationState = (
	emails: string[],
	ids: string[],
	maxEmails: number,
	userEmail: string | undefined,
	coreInvitesInJiraProjectEnabled?: boolean,
): ValidationState[] => {
	const isOverflowing = emails.concat(ids).length > maxEmails;
	const hasInvalidEmail = emails.some((v) => validateEmail(v) === 'INVALID');
	const isInvitingSelf = userEmail && emails.includes(userEmail);
	const validationList: ValidationState[] = [];
	if (isOverflowing) {
		validationList.push(
			coreInvitesInJiraProjectEnabled
				? VALIDATION_OVERFLOW_ERROR_FOR_PROJECTS
				: VALIDATION_OVERFLOW_ERROR,
		);
	}

	if (hasInvalidEmail) {
		validationList.push(VALIDATION_INVALID_EMAIL);
	}

	if (!coreInvitesInJiraProjectEnabled && isInvitingSelf) {
		validationList.push(VALIDATION_ERROR_INVITING_SELF);
	}
	if (validationList.length > 0) {
		return validationList;
	}

	return VALIDATION_DEFAULT;
};
